/**
 * Canonical doc page schema (PRD-3).
 *
 * One `<slug>.page.json` file per slug, colocated with referenced assets in
 * the same directory (ADR-004). The same metadata fields are also accepted
 * via YAML frontmatter on Markdown files for unmigrated material — the Vite
 * plugin auto-wraps the body into a single `rich-text` block before
 * validation. Whichever authoring format is used, every shipped page passes
 * through `DocPageSchema.safeParse` exactly once.
 *
 * Block primitives are reused from `@reflex/plugin-marketing` per
 * ADR-001. Live-aware block types (PRD-9a) are added to the same registry in
 * Workstream C; the schema below does not need to change for that.
 */
import { z } from 'zod';
import { BlocksSchema, type Block } from '@reflex/plugin-marketing/shared/blocks';

/**
 * Audiences a doc page may target. Drawn from PRD-3:
 *   - `end-user`  — both technical and non-technical end users; if a page
 *                   assumes coding skills it must declare `developer` instead.
 *   - `admin`     — org admins evaluating or rolling out Reflex.
 *   - `developer` — plugin developers and integrators.
 *   - `agent`     — LLMs/agents reading docs through MCP.
 *
 * A single page may target multiple audiences (e.g. `[developer, agent]` or
 * `[admin, end-user]`).
 */
export const DocAudienceSchema = z.enum(['end-user', 'admin', 'developer', 'agent']);
export type DocAudience = z.infer<typeof DocAudienceSchema>;

/**
 * Visibility tier (PRD-1). Two structural tiers only — there is no
 * `authenticated` tier. Workspace-aware reading is delivered through
 * live-aware blocks (PRD-9a) inside otherwise-public pages.
 */
export const DocVisibilitySchema = z.enum(['public', 'internal']);
export type DocVisibility = z.infer<typeof DocVisibilitySchema>;

/**
 * Content type (PRD-3). Drives navigation grouping and LLM-friendly
 * retrieval. `task` pages are short, action-oriented entries.
 */
export const DocContentTypeSchema = z.enum(['journey', 'reference', 'concept', 'task']);
export type DocContentType = z.infer<typeof DocContentTypeSchema>;

/**
 * Editorial status (PRD-3). `draft` and `review` pages are still shipped if
 * `visibility: public` — `status` exists for editorial workflows, not for
 * gating publication. `deprecated` pages remain returnable but should be
 * flagged in the UI.
 */
export const DocStatusSchema = z.enum(['draft', 'review', 'published', 'deprecated']);
export type DocStatus = z.infer<typeof DocStatusSchema>;

/**
 * Strict ISO-8601 calendar date (`YYYY-MM-DD`) for a named metadata field.
 * Matches the freshness gate's date semantics from PRD-3 / Freshness Gate 10a.
 */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
function isoCalendarDateSchema(field: string) {
  return z
    .string()
    .regex(ISO_DATE_RE, `${field} must be an ISO-8601 calendar date (YYYY-MM-DD)`)
    .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)), {
      message: `${field} must be a valid ISO-8601 date`,
    });
}

/** The date a page must next be reviewed by. */
export const DocReviewBySchema = isoCalendarDateSchema('reviewBy');

/**
 * The date a human last verified the page against the product. This is the
 * field the freshness gate measures elapsed time from — `reviewBy` alone can
 * be pushed into the future indefinitely without anyone reading the page, so
 * it cannot detect drift on its own.
 */
export const DocLastReviewedSchema = isoCalendarDateSchema('lastReviewed');

/**
 * Per-page controls for the prev/next pager rendered at the foot of an
 * article. Pages that are conceptually first or last in the corpus can
 * suppress one side without having to reshuffle the whole manifest.
 *
 * Both flags default to `false` — when omitted, the renderer derives
 * neighbours from manifest order as before.
 */
export const DocPagerControlsSchema = z.object({
  /** Hide the "Previous" link, e.g. on the very first doc in a corpus. */
  hidePrev: z.boolean().optional(),
  /** Hide the "Next" link, e.g. on a terminal reference page. */
  hideNext: z.boolean().optional(),
});
export type DocPagerControls = z.infer<typeof DocPagerControlsSchema>;

/**
 * Per-page metadata (PRD-3). Identical contract for JSON pages (where the
 * fields live as siblings of `blocks` on the page record) and for Markdown
 * pages (where the fields live in YAML frontmatter). Validation rules are
 * identical regardless of authoring format.
 */
export const DocPageMetadataSchema = z.object({
  title: z.string().min(1),
  audience: z.array(DocAudienceSchema).min(1),
  visibility: DocVisibilitySchema,
  contentType: DocContentTypeSchema,
  owner: z.string().min(1),
  status: DocStatusSchema,
  reviewBy: DocReviewBySchema,
  /**
   * ISO-8601 date (`YYYY-MM-DD`) a human last verified this page against the
   * shipped product. Required on every page: `pnpm check:docs` fails when it
   * is older than the review window, when it sits in the future, or when
   * `reviewBy` is scheduled more than one window past it. Bumping `reviewBy`
   * without also bumping this field no longer buys a page any time.
   */
  lastReviewed: DocLastReviewedSchema,
  /** Optional product-feature attribution. Drives Freshness Gate 10c later. */
  feature: z.string().min(1).optional(),
  /** Optional plugin attribution. Set automatically for plugin-contributed docs. */
  plugin: z.string().min(1).optional(),
  /** Optional agent-runtime attribution (e.g. `claude`, `opencode`). */
  agentType: z.string().min(1).optional(),
  /** One-sentence summary used for OG/`description` tags and `llms.txt`. */
  summary: z.string().min(1).optional(),
  /**
   * Optional ISO-8601 date (`YYYY-MM-DD`) the page's content was released.
   * Drives a date-window "New" badge in the docs UI (see `@reflex/ui`'s
   * `isRecentlyReleased`). Distinct from `reviewBy`, which is the next
   * mandatory review date.
   */
  releasedAt: DocReviewBySchema.optional(),
  /** Sort weight inside the page's nav group. Lower numbers sort first. */
  order: z.number().int().optional(),
  /**
   * Optional explicit sidebar group override. When set, the page appears
   * in this group instead of the default group derived from its slug's
   * first path segment (e.g. `demos/your-first-agent` → `demos`).
   * Useful for plugin-contributed pages that should appear in a custom
   * section like "Guided Demos" rather than "Plugins".
   */
  group: z.string().min(1).optional(),
  /**
   * Optional per-page overrides for the prev/next pager rendered by
   * `DocsPage`. Defaults to "show both sides when neighbours exist".
   */
  pager: DocPagerControlsSchema.optional(),
  /**
   * Block ids that should render *after* the prev/next pager instead of
   * inline with the rest of the page body. The renderer pulls these out
   * of the main `blocks` array and renders them in this order beneath the
   * pager — useful for a closing CTA banner that visually frames the
   * pager rather than getting buried above it.
   *
   * Ids that don't match any block are ignored silently so authors can
   * delete a block without having to remember to clean up this list.
   */
  footerBlockIds: z.array(z.string().min(1)).optional(),
});
export type DocPageMetadata = z.infer<typeof DocPageMetadataSchema>;

/**
 * Canonical on-disk shape (ADR-004). One `<slug>.page.json` per slug,
 * containing `metadata` plus a `blocks` array drawn from the marketing block
 * registry (ADR-001).
 */
export const DocPageSchema = z.object({
  metadata: DocPageMetadataSchema,
  blocks: BlocksSchema,
});
export type DocPage = z.infer<typeof DocPageSchema>;

/**
 * Markdown frontmatter input shape. Identical fields to `DocPageMetadata`
 * but more lenient on `audience` — single-string is coerced into a
 * one-element array so `audience: end-user` (no list syntax) is accepted in
 * YAML frontmatter as a convenience for hand-authored Markdown files.
 *
 * After parsing, the result is structurally equivalent to
 * `DocPageMetadataSchema` (i.e. `audience` is always `DocAudience[]`).
 */
export const DocPageFrontmatterSchema = DocPageMetadataSchema.extend({
  audience: z
    .union([DocAudienceSchema, z.array(DocAudienceSchema).min(1)])
    .transform((v) => (Array.isArray(v) ? v : [v])),
});
export type DocPageFrontmatter = z.infer<typeof DocPageFrontmatterSchema>;

/**
 * Convenience re-export for downstream callers that need to construct page
 * blocks programmatically (e.g. the Markdown auto-wrap path).
 */
export type DocBlock = Block;

/**
 * The set of `visibility` values a public manifest entry may carry. Today
 * the only shipped tier is `public`, but consumers should reach for this
 * constant rather than re-typing the string.
 */
export const SHIPPED_VISIBILITIES: readonly DocVisibility[] = ['public'] as const;
